import { setActiveBundle } from "containers/Bundles/Bundles.service";
import { getUserData } from "containers/Auth/Auth.selectors";
import { setActiveProject } from "containers/Projects/Projects.service";
import { resourceDataSelector } from "containers/Resources/Resources.selectors";
import { useDefaultLocation } from "containers/Resources/screens/ResourcesSingle/useDefaultLocation";
import PropTypes from "prop-types";
import qs from "qs";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useRouteMatch } from "react-router-dom";
import { useTitle } from "react-use";
import { canvasPathFragment, dashboardEditPathFragment } from "utils/RouterManager/constants";
import { getRecordPageTitle } from "utils/pageTitle";
import { applyRecordFavicon, resetFavicon } from "utils/favicon";
import { resolveScreenFaviconIcon } from "utils/RouterManager/screenFavicon";
import { getByRecordTypeName } from "components/Icon/Icon";
import { getIconSelector } from "components/ImageIcon/ImageIcon";
import { getWorkspaceData } from "containers/Workspaces/Workspaces.selectors";
import { useMatchExecutionFilterLocation } from "utils/RouterManager/utils";
import useReplaceIdWithName from "utils/useReplaceIdWithName";
import { prefersIdInUrl } from "utils/service";
import { getIri } from "utils/utils";
import { getBundlesById } from "containers/Bundles/Bundles.selectors";
import { getProjectsById } from "containers/Projects/Projects.selectors";

const RouterChangeHandler = ({ isNewRecord }) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const userData = useSelector(getUserData);
    useReplaceIdWithName(userData?.workspace_id, userData?.workspace_name);
    const match = useRouteMatch("/:recordTypeName");
    const recordTypeName = match?.params?.recordTypeName;
    const queryString = qs.parse(location.search.substring(1));
    const recordId = queryString?.id ?? queryString?.name;
    const iri = getIri(recordTypeName, recordId);
    const resourceData = useSelector(resourceDataSelector(iri));
    const shouldReplaceRecordUrlWithName =
        resourceData?.id &&
        resourceData?.name &&
        recordTypeName &&
        recordTypeName !== "workspace" &&
        !prefersIdInUrl(recordTypeName);
    useReplaceIdWithName(
        shouldReplaceRecordUrlWithName ? resourceData.id : null,
        shouldReplaceRecordUrlWithName ? resourceData.name : null
    );
    const isProject = recordTypeName === "project";
    const isBundle = recordTypeName === "bundle";
    const isWorkspace = recordTypeName === "workspace";
    const isCustomObject = recordTypeName === "custom_object";
    const projectsById = useSelector(getProjectsById);
    const bundlesById = useSelector(getBundlesById);
    const workspaceData = useSelector(getWorkspaceData);

    // The custom object's icon is configured on its OBJECT_TEMPLATE, not on the
    // CO record itself — resolve it via the SAME selector the CO list/nav uses
    // to render per-OT icons (getIconSelector -> object_template.icon data-URI).
    const customObjectId = isCustomObject ? resourceData?.id ?? null : null;
    const customObjectOtIconSelector = useMemo(
        () => (customObjectId ? getIconSelector("custom_object", customObjectId) : () => null),
        [customObjectId]
    );
    const customObjectOtIcon = useSelector(customObjectOtIconSelector);

    // The rendered dashboard is the DEFAULT for a dashboard record; the config
    // editor is the flagged `?edit` mode. In view mode DashboardView sets its own
    // page title, so RouterChangeHandler must not also drive it from resourceData.
    const isDashboardView =
        recordTypeName === "dashboard" &&
        !isNewRecord &&
        !(dashboardEditPathFragment in queryString);

    const matchCanvasPage = useRouteMatch(`/:recordTypeName/:recordId/${canvasPathFragment}`);
    const canvasFromQuery = "canvas" in queryString;
    const canvasLocation = canvasFromQuery ? recordTypeName : matchCanvasPage?.params?.recordTypeName;
    const canvasLocationId = (() => {
        if (canvasFromQuery) {
            if (queryString.id) {
                return queryString.id;
            }
            if (queryString.name && canvasLocation === "project") {
                return Object.values(projectsById).find((p) => p.name === queryString.name)?.id;
            }
            if (queryString.name && canvasLocation === "bundle") {
                return Object.values(bundlesById).find((b) => b.name === queryString.name)?.id;
            }
            return null;
        }
        return matchCanvasPage?.params?.recordId;
    })();

    const { matchExecutionFilterPage, executionFilterLocation, executionFilterLocationId } =
        useMatchExecutionFilterLocation();

    const pageTitle = isDashboardView
        ? null
        : resourceData
          ? getRecordPageTitle(resourceData)
          : "Cloudomation";
    useTitle(pageTitle ?? document.title);

    // Reflect the currently-viewed screen/record in the browser-tab favicon.
    // Precedence:
    //   1. A known NON-record screen (execution monitor, advanced search, logs,
    //      settings, storage analysis, canvas, bundle manager, trash, …) — show
    //      that screen's own nav glyph. Checked first so, e.g., a container's
    //      ?logs sub-page shows the logs icon rather than the container icon.
    //   2. The WORKSPACE home screen — show the workspace's own custom icon
    //      (which lives on the workspace record, not on `resourceData.icon`).
    //   3. A single record with data — its user icon, else the record-type
    //      default. For a CUSTOM_OBJECT the user icon is the OT-configured icon.
    //   4. Otherwise reset to the app default.
    useEffect(() => {
        // In dashboard VIEW mode the DashboardView screen owns the favicon (it
        // fetches the dashboard record itself, so `resourceData` is empty here and
        // this effect would otherwise reset to the app default). Defer to it —
        // symmetric with the page-title back-off above.
        if (isDashboardView) {
            return;
        }
        const screenIcon = resolveScreenFaviconIcon(location.pathname, location.search);
        if (screenIcon) {
            applyRecordFavicon(undefined, screenIcon);
            return;
        }
        if (isWorkspace) {
            applyRecordFavicon(
                workspaceData?.icon ?? resourceData?.icon,
                getByRecordTypeName("workspace")
            );
            return;
        }
        if (resourceData && recordTypeName) {
            const typeDefaultIcon = getByRecordTypeName(recordTypeName);
            const userIcon = isCustomObject
                ? customObjectOtIcon ??
                  resourceData.object_template_id_object_template?.icon ??
                  resourceData.icon
                : resourceData.icon;
            applyRecordFavicon(userIcon, typeDefaultIcon);
            return;
        }
        resetFavicon();
    }, [
        isDashboardView,
        resourceData,
        recordTypeName,
        isWorkspace,
        isCustomObject,
        customObjectOtIcon,
        workspaceData,
        location.pathname,
        location.search,
    ]);

    const { location: newRecordLocation, locationId: newRecordLocationId } = useDefaultLocation({
        data: queryString,
        resourceName: recordTypeName ?? "workspace",
    });

    // this may look complicated at first glance, but is rather simple indeed
    // we need to set the active project and bundle based on the current route
    // when we set one of them, the other has to be unset
    // we also need to set the active project and bundle to null if
    // the current route is not a project or bundle (trash, search, etc.)
    useEffect(() => {
        if (resourceData) {
            const activeId = resourceData.deleted_at ? null : resourceData.id;
            if (isProject) {
                // project page
                setActiveProject(dispatch, activeId);
                setActiveBundle(dispatch, null);
            } else if (isBundle) {
                // bundle page
                setActiveBundle(dispatch, activeId);
                setActiveProject(dispatch, null);
            } else {
                const getActiveId = (recordType) =>
                    resourceData[`${recordType}_id_${recordType}`]?.deleted_at
                        ? null
                        : resourceData[`${recordType}_id`];
                if (resourceData.project_id) {
                    // resource that has a project
                    setActiveProject(dispatch, getActiveId("project"));
                    setActiveBundle(dispatch, null);
                } else if (resourceData.bundle_id) {
                    // resource that has a bundle
                    setActiveBundle(dispatch, getActiveId("bundle"));
                    setActiveProject(dispatch, null);
                } else {
                    // resource that has no project or bundle, e.g. workspace
                    setActiveProject(dispatch, null);
                    setActiveBundle(dispatch, null);
                }
            }
        } else {
            if (isNewRecord) {
                if (newRecordLocation && newRecordLocationId) {
                    const hasProjectLocation = newRecordLocation === "project_id";
                    if (hasProjectLocation) {
                        setActiveProject(dispatch, newRecordLocationId);
                        setActiveBundle(dispatch, null);
                    } else {
                        setActiveProject(dispatch, null);
                        setActiveBundle(dispatch, newRecordLocationId);
                    }
                } else if (!isBundle && !isProject) {
                    // resource that has no project or bundle, e.g. workspace
                    setActiveProject(dispatch, null);
                    setActiveBundle(dispatch, null);
                }
                return;
            }
            if (matchCanvasPage || canvasFromQuery) {
                switch (canvasLocation) {
                    case "project":
                        setActiveProject(dispatch, canvasLocationId);
                        setActiveBundle(dispatch, null);
                        break;
                    case "bundle":
                        setActiveProject(dispatch, null);
                        setActiveBundle(dispatch, canvasLocationId);
                        break;
                    default:
                        setActiveProject(dispatch, null);
                        setActiveBundle(dispatch, null);
                }
            } else {
                if (matchExecutionFilterPage) {
                    if (executionFilterLocation === "project") {
                        setActiveProject(dispatch, executionFilterLocationId);
                        setActiveBundle(dispatch, null);
                    } else if (executionFilterLocation === "bundle") {
                        setActiveProject(dispatch, null);
                        setActiveBundle(dispatch, executionFilterLocationId);
                    } else {
                        setActiveProject(dispatch, null);
                        setActiveBundle(dispatch, null);
                    }
                    return;
                }
                // if we don't have resource data, we are probably on a search or trash page
                setActiveProject(dispatch, null);
                setActiveBundle(dispatch, null);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        canvasLocation,
        canvasLocationId,
        canvasFromQuery,
        dispatch,
        isBundle,
        isNewRecord,
        isProject,
        recordId,
        resourceData,
        executionFilterLocation,
        executionFilterLocationId,
        newRecordLocation,
        newRecordLocationId,
    ]);

    return null;
};

RouterChangeHandler.defaultProps = {};

RouterChangeHandler.propTypes = {
    isNewRecord: PropTypes.bool,
};

export default RouterChangeHandler;
